"use client";

import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { LogoLockup } from "./Logo";
import MobileAuthSection from "./MobileAuthSection";
import LanguageToggle from "./LanguageToggle";
import { StaysIcon, FurnishIcon, ExperiencesIcon } from "./NavIcons";

// Mobile drawer shortcuts — skip the "Pick your zone" intermediate page and
// link straight into /search with the area filter pre-applied.
const COMPOUND_LINKS: { label: string; href: string }[] = [
  { label: "Marassi", href: "/search?area=marassi" },
  { label: "Fouka Bay", href: "/search?area=fouka-bay" },
];

export default function MobileNav() {
  const [open, setOpen] = useState(false);
  const [wishCount, setWishCount] = useState(0);
  const t = useTranslations("mobileNav");
  const tc = useTranslations("common");
  const tn = useTranslations("nav");

  // The three sections first, with the same line icons as the header tabs
  // (NavIcons.tsx); the utility links keep their emoji tiles below.
  const SECTION_LINKS: { label: string; href: string | null; icon: ReactNode; sub?: string }[] = [
    { label: tn("stays"), href: "/search", icon: <StaysIcon />, sub: t("browseStaysSub") },
    { label: tn("furnish"), href: "/furnish", icon: <FurnishIcon />, sub: t("furnishSub") },
    { label: tn("experiences"), href: "/experiences", icon: <ExperiencesIcon />, sub: t("experiencesSub") },
  ];
  const MAIN_LINKS: { label: string; href: string; emoji: string; sub?: string }[] = [
    { label: tc("wishlist"), href: "/wishlist", emoji: "♡", sub: t("wishlistSub") },
    { label: t("communities"), href: "/compounds", emoji: "🏘️", sub: t("communitiesSub") },
    { label: tc("contactUs"), href: "/contact", emoji: "✉️", sub: t("contactUsSub") },
    { label: t("aboutUs"), href: "/about", emoji: "ⓘ", sub: t("aboutUsSub") },
    { label: t("faq"), href: "/faq", emoji: "💬", sub: t("faqSub") },
  ];

  useEffect(() => {
    const read = () => {
      try {
        const raw = localStorage.getItem("ss-wishlist");
        setWishCount(raw ? JSON.parse(raw).length : 0);
      } catch {
        setWishCount(0);
      }
    };
    read();
    window.addEventListener("storage", read);
    window.addEventListener("ss:wishlist", read);
    return () => {
      window.removeEventListener("storage", read);
      window.removeEventListener("ss:wishlist", read);
    };
  }, []);

  // Lock body scroll while the drawer is open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        aria-label={t("openMenu")}
        onClick={() => setOpen(true)}
        className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-ss-bg-soft cursor-pointer"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Portal to <body>: the drawer is position:fixed, but the sticky header
          it's mounted inside has `backdrop-blur`, which (per spec + iOS Safari)
          makes that header the containing block for fixed descendants — so the
          drawer was being sized to the 70px header, collapsing its flex-1 body.
          Rendering into <body> escapes that containing block. */}
      {open && createPortal(
        <>
          {/* Backdrop. z-[60] to clear the sticky header (z-50) so the drawer
              fully overlays it on desktop too. */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] fade-up"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          {/* Drawer */}
          <div className="fixed top-0 end-0 h-full w-[86%] max-w-[360px] bg-white z-[60] flex flex-col shadow-[var(--shadow-ss-xl)]">
            <div className="flex items-center justify-between px-5 h-[70px] border-b border-ss-line">
              <LogoLockup size={20} />
              <button
                type="button"
                aria-label={t("closeMenu")}
                onClick={() => setOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-ss-bg-soft text-xl leading-none cursor-pointer"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Auth first — sign in / create account when signed out, or
                  profile + account links + sign out when signed in. Anchored at
                  the top of the drawer so it's immediately visible (it used to
                  sit below the nav links and was easy to miss). */}
              <MobileAuthSection onClose={() => setOpen(false)} />

              {/* Sections */}
              <nav className="flex flex-col p-3 pb-0 gap-1" aria-label={tn("sectionsAria")}>
                {SECTION_LINKS.map((l) => {
                  const inner = (
                    <>
                      <span className="w-11 h-11 rounded-[12px] bg-ss-bg-soft text-ss-blue grid place-items-center shrink-0">{l.icon}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[15px] font-semibold leading-tight">
                          {l.label}
                          {!l.href && (
                            <span className="ms-2 align-middle text-[9px] font-bold uppercase tracking-wider bg-ss-bg-soft text-ss-soft px-1.5 py-0.5 rounded-full">{tn("soon")}</span>
                          )}
                        </span>
                        {l.sub && <span className="block text-[12.5px] text-ss-muted mt-0.5">{l.sub}</span>}
                      </span>
                      {l.href && <span className="text-ss-soft rtl:rotate-180" aria-hidden>›</span>}
                    </>
                  );
                  return l.href ? (
                    <Link key={l.label} href={l.href} onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-[12px] hover:bg-ss-bg-soft text-ss-text">
                      {inner}
                    </Link>
                  ) : (
                    <div key={l.label} className="flex items-center gap-3 px-3 py-3 rounded-[12px] text-ss-soft cursor-default select-none">
                      {inner}
                    </div>
                  );
                })}
              </nav>
              <div className="mx-6 my-2 border-t border-ss-line" />
              {/* Main links */}
              <nav className="flex flex-col p-3 gap-1">
                {MAIN_LINKS.map((l) => (
                  <Link
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-[12px] hover:bg-ss-bg-soft text-ss-text"
                  >
                    <span className="w-9 h-9 grid place-items-center rounded-[10px] bg-ss-blue-50 text-lg flex-none">
                      {l.emoji}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-semibold text-[14.5px]">
                        {l.label}
                        {l.href === "/wishlist" && wishCount > 0 && (
                          <span className="ms-2 inline-flex items-center justify-center bg-ss-blue text-white text-[10.5px] font-bold rounded-full min-w-[18px] h-[18px] px-1">
                            {wishCount}
                          </span>
                        )}
                      </span>
                      {l.sub && (
                        <span className="block text-[12px] text-ss-muted truncate">
                          {l.sub}
                        </span>
                      )}
                    </span>
                    <span className="text-ss-line rtl:-scale-x-100 inline-block">›</span>
                  </Link>
                ))}
              </nav>

              {/* Compound shortcuts */}
              <div className="px-4 pt-2 pb-1">
                <div className="text-[10.5px] font-bold uppercase tracking-wider text-ss-muted px-1 pb-1.5">
                  {t("popularCompounds")}
                </div>
                <div className="flex flex-wrap gap-2">
                  {COMPOUND_LINKS.map((c) => (
                    <Link
                      key={c.href}
                      href={c.href}
                      onClick={() => setOpen(false)}
                      className="px-3 py-1.5 rounded-full bg-ss-bg-soft hover:bg-ss-blue-50 hover:text-ss-blue text-[13px] font-semibold text-ss-text"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Secondary links — only items NOT already in the main list
                  above. About us / FAQ live up there now to surface them
                  faster. */}
              <div className="px-4 pt-4 pb-2 mt-2 border-t border-ss-line space-y-0.5">
                <Link
                  href="/trust"
                  onClick={() => setOpen(false)}
                  className="block px-1 py-2 text-[13.5px] text-ss-muted hover:text-ss-text"
                >
                  {tc("trustSafety")}
                </Link>
                <LanguageToggle className="block px-1 py-2 text-[13.5px] text-ss-muted hover:text-ss-text font-semibold transition-colors" />
              </div>
            </div>

            {/* WhatsApp pinned footer */}
            <div className="p-4 border-t border-ss-line bg-white">
              <a
                href="https://wa.me/201044414367"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-[#25d366] text-white px-4 py-3.5 rounded-[12px] font-semibold hover:bg-[#1ebe5a] transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.52 3.48A11.94 11.94 0 0012.04 0C5.5 0 .18 5.32.18 11.86c0 2.09.55 4.13 1.6 5.93L0 24l6.36-1.66a11.86 11.86 0 005.68 1.45h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.23-6.15-3.39-8.45z" />
                </svg>
                {t("whatsappConcierge")}
              </a>
              <p className="text-[12px] text-ss-muted text-center mt-2">
                {t("whatsappReplies")}
              </p>
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
